import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const btn = "w-full py-3 rounded font-bold text-sm";

// PUBLIC caller page — opened from the secure link sent by dispatch. No login required.
export default function CallerLink() {
  const { token } = useParams();
  const [s, setS] = useState(null);
  const [chat, setChat] = useState([]);
  const [text, setText] = useState("");
  const [sharingLoc, setSharingLoc] = useState(false);
  const [videoOn, setVideoOn] = useState(false);
  const [status, setStatus] = useState("");
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const frameTimer = useRef(null);
  const locWatch = useRef(null);
  const fileRef = useRef(null);

  const load = () => axios.get(`${API}/caller/${token}`).then((r) => { setS(r.data); setChat(r.data.chat || []); }).catch(() => setS({ error: true }));
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [token]); // eslint-disable-line
  useEffect(() => () => { stopVideo(); stopLocation(); }, []); // eslint-disable-line

  const consent = (granted) => axios.post(`${API}/caller/${token}/consent`, { granted }).then(load);
  const send = async () => {
    if (!text.trim()) return;
    await axios.post(`${API}/caller/${token}/chat`, { text: text.trim() });
    setText(""); load();
  };
  const shareLocation = () => {
    if (!navigator.geolocation) { setStatus("Location not supported on this device"); return; }
    setSharingLoc(true);
    locWatch.current = navigator.geolocation.watchPosition(
      (p) => axios.post(`${API}/caller/${token}/location`, { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }).catch(() => {}),
      () => { setStatus("Location permission denied"); setSharingLoc(false); },
      { enableHighAccuracy: true, maximumAge: 5000 });
  };
  const stopLocation = () => { if (locWatch.current != null) navigator.geolocation.clearWatch(locWatch.current); setSharingLoc(false); };
  const startVideo = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 480 }, audio: false });
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setVideoOn(true);
      const canvas = document.createElement("canvas");
      frameTimer.current = setInterval(() => {
        const v = videoRef.current;
        if (!v || !v.videoWidth) return;
        canvas.width = 320; canvas.height = Math.round(320 * v.videoHeight / v.videoWidth);
        canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
        axios.post(`${API}/caller/${token}/frame`, { image: canvas.toDataURL("image/jpeg", 0.45) }).catch(() => {});
      }, 2000);
    } catch { setStatus("Camera permission denied"); }
  };
  const stopVideo = () => {
    clearInterval(frameTimer.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    setVideoOn(false);
    axios.post(`${API}/caller/${token}/video-stop`).catch(() => {});
  };
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    setStatus("Uploading…");
    try { await axios.post(`${API}/caller/${token}/upload`, fd); setStatus("Upload received by dispatch ✓"); }
    catch { setStatus("Upload failed — try a smaller file"); }
    e.target.value = "";
  };

  if (!s) return <div className="min-h-screen bg-[#0b1220] text-white flex items-center justify-center font-mono text-sm">Loading secure session…</div>;
  if (s.error || s.ended) return (
    <div className="min-h-screen bg-[#0b1220] text-white flex items-center justify-center p-6 text-center">
      <div><div className="text-lg font-bold mb-2">Session ended</div>
        <div className="text-sm text-white/60">This secure link is no longer active. If you still need help, call 911.</div></div>
    </div>);

  const wants = (k) => (s.requested || []).includes(k);
  return (
    <div className="min-h-screen bg-[#0b1220] text-white p-4 max-w-md mx-auto" data-testid="caller-page">
      <div className="text-center mb-4">
        <div className="font-bold text-base">{s.agency}</div>
        <div className="font-mono text-[11px] text-[#22D3EE]">SECURE EMERGENCY MEDIA LINK · CALL {s.cc_number}</div>
      </div>
      {!s.consent ? (
        <div className="border border-[#22D3EE]/40 bg-[#0d1a30] rounded-lg p-4" data-testid="caller-consent">
          <div className="text-sm leading-relaxed mb-4">“{s.consent_text}”</div>
          <div className="text-[11px] text-white/60 mb-4">Dispatch is requesting: {(s.requested || []).map((r) => r.replace("_", " ")).join(", ")}. Sharing is voluntary. You can stop at any time. Nothing is accessed without your permission.</div>
          <button data-testid="caller-consent-yes" onClick={() => consent(true)} className={btn + " bg-[#10B981] text-black mb-2"}>I AGREE — SHARE WITH DISPATCH</button>
          <button data-testid="caller-consent-no" onClick={() => consent(false)} className={btn + " border border-[#EF4444] text-[#EF4444]"}>NO — DECLINE</button>
        </div>
      ) : !s.consent.granted ? (
        <div className="text-center text-sm text-white/70 border border-white/10 rounded-lg p-4">You declined media sharing. You may close this page. If you change your mind, ask the dispatcher for a new link.</div>
      ) : (
        <div className="space-y-3">
          {wants("live_video") && (
            <div className="border border-white/10 rounded-lg p-3">
              <div className="font-mono text-[10px] text-white/50 uppercase mb-1.5">Live video {videoOn && <span className="text-[#EF4444] animate-pulse">● SHARING</span>}</div>
              <video ref={videoRef} muted playsInline className={`w-full rounded ${videoOn ? "" : "hidden"}`} />
              {!videoOn
                ? <button data-testid="caller-video-start" onClick={startVideo} className={btn + " bg-[#22D3EE] text-black"}>START SHARING VIDEO</button>
                : <button data-testid="caller-video-stop" onClick={stopVideo} className={btn + " bg-[#EF4444] text-white mt-2"}>STOP SHARING — you may stop at any time</button>}
            </div>
          )}
          {wants("location") && (
            <div className="border border-white/10 rounded-lg p-3">
              <div className="font-mono text-[10px] text-white/50 uppercase mb-1.5">My location</div>
              {!sharingLoc
                ? <button data-testid="caller-loc-start" onClick={shareLocation} className={btn + " bg-[#10B981] text-black"}>SHARE MY LOCATION</button>
                : <button data-testid="caller-loc-stop" onClick={stopLocation} className={btn + " border border-[#F59E0B] text-[#F59E0B]"}>SHARING LOCATION — TAP TO STOP</button>}
            </div>
          )}
          {(wants("photo") || wants("video")) && (
            <div className="border border-white/10 rounded-lg p-3">
              <div className="font-mono text-[10px] text-white/50 uppercase mb-1.5">Send photo / video</div>
              <button data-testid="caller-upload-btn" onClick={() => fileRef.current?.click()} className={btn + " bg-[#A855F7] text-white"}>UPLOAD PHOTO OR VIDEO</button>
              <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={upload} />
            </div>
          )}
          {(wants("silent_chat") || wants("accessibility")) && (
            <div className="border border-white/10 rounded-lg p-3">
              <div className="font-mono text-[10px] text-white/50 uppercase mb-1.5">Silent chat with dispatch</div>
              <div className="h-36 overflow-y-auto bg-black/30 rounded p-2 mb-2" data-testid="caller-chat">
                {chat.map((m, i) => (
                  <div key={i} className={`text-[12px] py-0.5 ${m.from === "caller" ? "text-[#22D3EE] text-right" : "text-white"}`}>
                    <b>{m.from === "caller" ? "You" : "Dispatch"}:</b> {m.text}
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input data-testid="caller-chat-input" value={text} onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type quietly…"
                  className="flex-1 bg-black/40 border border-white/20 rounded px-2 py-2 text-sm outline-none" />
                <button data-testid="caller-chat-send" onClick={send} className="px-4 rounded bg-[#22D3EE] text-black font-bold text-sm">SEND</button>
              </div>
            </div>
          )}
          {status && <div className="font-mono text-[11px] text-[#F59E0B] text-center" data-testid="caller-status">{status}</div>}
          <div className="text-center text-[10px] text-white/40">Everything you share goes only to emergency dispatch and responding personnel. All access is logged.</div>
        </div>
      )}
    </div>
  );
}
